import React, { useEffect, useMemo, useState, useContext } from "react";
import {Card, Form, Button, Spinner, Table, Badge, Col, InputGroup} from "react-bootstrap";
import { toast } from "react-toastify";
import { Context } from "../../index";

// ✅ Use real API from userAPI
import {
    uploadPromoTemp,
    createPromoBountyRequestApi,
    fetchPromoBountyRequestsApi,
    // moderatePromoBountyApi, // (admin only)
} from "../../http/userAPI";
import Row from "react-bootstrap/Row";

// 🧮 Client‑side estimation (display only; server is the source of truth)
const BOUNTY = {
    base: 300,
    activeLinkBonus: 200,
    videoBonus: 400,
};

// 🔎 url → platform label
const domainToPlatform = (url) => {
    try {
        const host = new URL(url).hostname.replace("www.", "");
        if (host.includes("youtube")) return "YouTube";
        if (host.includes("facebook")) return "Facebook";
        if (host.includes("instagram")) return "Instagram";
        if (host.includes("reddit")) return "Reddit";
        if (host.includes("x.com") || host.includes("twitter")) return "X / Twitter";
        if (host.includes("tiktok")) return "TikTok";
        if (host.includes("medium")) return "Medium";
        if (host.includes("quora")) return "Quora";
        if (host.includes("linkedin")) return "LinkedIn";
        return host;
    } catch {
        return "—";
    }
};

const fileToDataUrl = (file) =>
    new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
    });

const MAX_FILE_MB = 6;
const ALLOWED_IMG = ["image/png", "image/jpeg", "image/webp"];

const VirtalibryPromoBounty = () => {
    const { user } = useContext(Context);
    const userId = user?.user?.id;

    // form state
    const [postUrl, setPostUrl] = useState("");
    const [screenshotPreview, setScreenshotPreview] = useState(null);
    const [tempShot, setTempShot] = useState(null); // { fileName, hash }
    const [confirmNameSpelling, setConfirmNameSpelling] = useState(false);
    const [confirmPublic, setConfirmPublic] = useState(true);
    const [isActiveLink, setIsActiveLink] = useState(false);
    const [isVideo, setIsVideo] = useState(false);
    const [note, setNote] = useState("");

    // ui state
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(new Set());

    const toggle = (id) =>
        setOpen((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    // Estimated reward (informational only)
    const estimated = useMemo(() => {
        let sum = BOUNTY.base;
        if (isActiveLink) sum += BOUNTY.activeLinkBonus;
        if (isVideo) sum += BOUNTY.videoBonus;
        return sum;
    }, [isActiveLink, isVideo]);

    // load requests
    const fetchRows = async () => {
        try {
            const data = await fetchPromoBountyRequestsApi(userId);
            // Normalize keys for table consumption
            const normalized = (data?.requests || []).map((r) => ({
                id: r.id,
                created_at: r.createdAt || r.created_at || r.updatedAt,
                platform: r.platform || domainToPlatform(r.post_url),
                url: r.post_url,
                // show verified if available (after moderation), otherwise the user's claim
                is_active_link: r.verified_active_link || r.is_active_link_claimed || false,
                is_video: (typeof r.verified_video_authorship === 'boolean') ? r.verified_video_authorship : !!r.is_video,
                est_payout: r.estimated_payout,
                final_payout: r.final_payout,
                status: r.status,
                moderator_note: r.moderator_note,
                screenshot_path: r.screenshot_path,
            }));
            setRows(normalized);
        } catch (e) {
            toast.error("Failed to load promo submissions", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) fetchRows();
    }, [userId]);

    const isValidUrl = (value) => {
        try {
            const u = new URL(value);
            return ["http:", "https:"].includes(u.protocol);
        } catch {
            return false;
        }
    };

    const onPickFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!ALLOWED_IMG.includes(f.type)) {
            toast.warning("Please upload PNG/JPG/WEBP screenshot.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }
        if (f.size > MAX_FILE_MB * 1024 * 1024) {
            toast.warning(`File is too large. Max ${MAX_FILE_MB} MB.`, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }
        try {
            // 1) local preview
            const url = await fileToDataUrl(f);
            setScreenshotPreview(url);
            // 2) upload to temp immediately
            const { uploadedFile, fileHash } = await uploadPromoTemp(f);
            setTempShot({ fileName: uploadedFile, hash: fileHash });
        } catch (err) {
            setScreenshotPreview(null);
            setTempShot(null);
            toast.error("Upload failed. Try another image.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        }
    };

    const resetForm = () => {
        setPostUrl("");
        setScreenshotPreview(null);
        setTempShot(null);
        setConfirmNameSpelling(false);
        setConfirmPublic(true);
        setIsActiveLink(false);
        setIsVideo(false);
        setNote("");
    };

    const canSubmit = !!userId && isValidUrl(postUrl) && !!tempShot?.fileName && confirmNameSpelling && confirmPublic;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
            toast.warning("Please complete all required fields.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }
        try {
            setSubmitting(true);
            await createPromoBountyRequestApi({
                userId: userId,
                postUrl: postUrl.trim(),
                isActiveLinkClaimed: isActiveLink,
                isVideo,
                note: note.trim(),
                tempFileName: tempShot.fileName,
                fileHash: tempShot.hash,
            });
            toast.success("Submitted for moderation.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            resetForm();
            fetchRows();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Submission failed", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card className="p-3 w-100 mt-0">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                <h5 className="text-muted mb-0" title="Earn extra Virt by promoting Virtalibry">Earn Extra Virt — Promote Virtalibry</h5>
                <small className="text-muted">Share your experience with <strong>Virtalibry</strong> across the internet and get rewarded. Each unique post is paid once after moderation.</small>
            </div>

            <div className="alert alert-info small mt-3" role="note">
                <div className="fw-semibold mb-1">How it works</div>
                <ul className="mb-2 ps-3">
                    <li>Write a short post/comment/review anywhere: YouTube, Facebook/Instagram, Reddit, forums, blogs, communities — your choice.</li>
                    <li><strong>Required:</strong> mention the service name exactly as <strong>Virtalibry</strong> (capital V, correct spelling).</li>
                    <li><strong>Optional but increases reward:</strong> add a clickable link to our site (including your partner link is OK): <code>https://virtalibry.com</code>.</li>
                    <li>Make sure your post is public (visible without login, if possible), then upload one screenshot and the direct URL to the post.</li>
                    <li><strong>Video option:</strong> You can also record a short screencast or video testimonial and publish it on your TikTok, YouTube, or another video platform. Send us the video link and a screenshot from your creator dashboard (e.g., YouTube Studio) that clearly confirms it’s your own video. In this case, your payout will be even higher.</li>
                </ul>
                <div className="mb-0">We’ll verify your submission. If the link is active/clickable and leads to our site, you’ll receive a higher payout.</div>
            </div>

            <Form onSubmit={onSubmit} className="d-flex gap-3 flex-wrap align-items-end w-100">
                <Row className="g-2 w-100 align-items-end">
                    {/* Ссылка + кнопка Open Link */}
                    <Col md={8}>
                        <Form.Group controlId="promoUrl">
                            <Form.Label className="small text-muted mb-1">Direct link to your post or video:
                                {" "}{postUrl && isValidUrl(postUrl) && (
                                    <>
                                        (Platform: {domainToPlatform(postUrl)})
                                    </>
                                )}
                            </Form.Label>
                            <InputGroup size="sm">
                                <Form.Control
                                    type="url"
                                    placeholder="https://..."
                                    value={postUrl}
                                    onChange={(e) => setPostUrl(e.target.value)}
                                    required
                                    isInvalid={postUrl.length > 0 && !isValidUrl(postUrl)}
                                />
                                <Button
                                    variant="outline-secondary"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (isValidUrl(postUrl)) window.open(postUrl, "_blank", "noopener,noreferrer");
                                    }}
                                    disabled={!isValidUrl(postUrl)}
                                >
                                    Open Link
                                </Button>
                            </InputGroup>
                            <Form.Control.Feedback type="invalid">
                                Please enter a valid URL (http/https).
                            </Form.Control.Feedback>

                        </Form.Group>
                    </Col>

                    {/* Комментарий справа */}
                    <Col md={4}>
                        <Form.Group controlId="promoNote">
                            <Form.Label className="small text-muted mb-1">Optional note for moderators</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={1}
                                size="sm"
                                placeholder="Context, where you posted, etc."
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                {/* Секция загрузки скриншота — отдельная строка */}
                <Row className="w-100 mt-2">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label className="small text-muted mb-1">
                                Screenshot (PNG/JPG/WEBP, up to {MAX_FILE_MB} MB)
                            </Form.Label>
                            <Form.Control
                                size="sm"
                                type="file"
                                accept={ALLOWED_IMG.join(",")}
                                onChange={onPickFile}
                                required
                            />
                            {screenshotPreview && (
                                <div className="mt-2 border rounded-2 p-1 bg-light" style={{ maxWidth: 460 }}>
                                    <img
                                        src={screenshotPreview}
                                        alt="screenshot preview"
                                        className="img-fluid rounded-2"
                                    />
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>


                {/* Чекбоксы */}
                <Form.Group className="mt-2">
                    <Form.Check
                        className="custom-checkbox mt-2"
                        type="checkbox"
                        id="confirmNameSpelling"
                        label={
                            <span className="small">
        I confirm my post mentions <strong>Virtalibry</strong> with correct spelling.
      </span>
                        }
                        checked={confirmNameSpelling}
                        onChange={(e) => setConfirmNameSpelling(e.target.checked)}
                        required
                    />
                    <Form.Check
                        className="custom-checkbox mt-1"
                        type="checkbox"
                        id="confirmPublic"
                        label={
                            <span className="small">
        My post is public and accessible via the link above.
      </span>
                        }
                        checked={confirmPublic}
                        onChange={(e) => setConfirmPublic(e.target.checked)}
                        required
                    />
                    <Form.Check
                        className="custom-checkbox mt-1"
                        type="checkbox"
                        id="isActiveLink"
                        label={
                            <span className="small">
        The post contains an <u>active clickable</u> link to virtalibry.com (including partner links).
      </span>
                        }
                        checked={isActiveLink}
                        onChange={(e) => setIsActiveLink(e.target.checked)}
                    />
                    <Form.Check
                        className="custom-checkbox mt-1"
                        type="checkbox"
                        id="isVideo"
                        label={
                            <span className="small">
        This is a video post (TikTok, YouTube, etc.) with my own authorship proof screenshot.
      </span>
                        }
                        checked={isVideo}
                        onChange={(e) => setIsVideo(e.target.checked)}
                    />


                </Form.Group>
                <Row className="w-100 mt-1">
                    <Col md={12}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="small text-muted">
                                <span className="me-2">Estimated reward:</span>
                                <Badge
                                    bg={isActiveLink || isVideo ? "success" : "secondary"}
                                    className="me-2"
                                >
                                    {estimated} Virt
                                </Badge>
                                <span className="fst-italic">(final amount is set by moderation rules)</span>
                            </div>

                            <Button
                                type="submit"
                                size="sm"
                                className="btn-success"
                                disabled={!canSubmit || submitting}
                            >
                                {submitting ? <Spinner animation="border" size="sm" /> : "Submit for Moderation"}
                            </Button>
                        </div>
                    </Col>
                </Row>


            </Form>

            {/* TABLE */}
            <div className="w-100" style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: "8px" }}>
                <hr />
                <strong className="text-danger mt-2 small">Your Promo Submissions:</strong>
                {loading ? (
                    <div className="text-muted small">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="text-muted small">No promo submissions yet.</div>
                ) : (
                    <Table bordered hover size="sm" stclassName="custom-checkbox mt-1" style={{ minWidth: "760px", lineHeight: 1.2 }}>
                        <thead className="table-light text-muted small">
                        <tr>
                            <th className="text-center small">ID</th>
                            <th className="text-start small">Date</th>
                            <th className="text-start small">Platform</th>
                            <th className="text-start small">Post URL</th>
                            <th className="text-center small">Active Link</th>
                            <th className="text-center small">Est. Payout</th>
                            <th className="text-center small">Status</th>
                            <th className="text-center small">Moderator Note</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((r) => (
                            <React.Fragment key={r.id}>
                                <tr
                                    onClick={() => toggle(r.id)}
                                    style={{ cursor: "pointer" }}
                                    className={open.has(r.id) ? "table-active small" : "small"}
                                >
                                    <td className="text-center align-middle text-muted small">{open.has(r.id) ? "▾" : "▸"} {r.id}</td>
                                    <td className="text-start align-middle text-muted small">
                                        {new Date(r.created_at).toLocaleString("en-GB", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false,
                                        }).replace(",", "")}
                                    </td>
                                    <td className="text-start align-middle text-muted small">{r.platform}</td>
                                    <td className="text-start align-middle text-muted small" style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>
                                        <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-decoration-underline">{r.url}</a>
                                    </td>
                                    <td className="text-center align-middle text-muted small">
                                        {r.is_active_link === true
                                            ? "Yes"
                                            : r.is_active_link === false
                                                ? "No"
                                                : "—"}
                                    </td>


                                    <td className="text-center text-success fw-bold align-middle text-muted small">{typeof r.final_payout === 'number' ? `${r.final_payout} Virt` : `${r.est_payout ?? (r.is_active_link ? BOUNTY.base + BOUNTY.activeLinkBonus : BOUNTY.base)} Virt`}</td>
                                    <td className="text-center align-middle text-muted small">
                      <span className={`text-uppercase  small fw-bold ${r.status === "approved" ? "text-success" : r.status === "pending" ? "text-warning" : r.status === "rejected" ? "text-danger" : "text-secondary"}`}>
                        {String(r.status)}
                      </span>
                                    </td>
                                    <td className="text-center align-middle text-muted small">{r.moderator_note ? (open.has(r.id) ? "Click to hide" : "Click to view") : "—"}</td>
                                </tr>
                                {open.has(r.id) && (
                                    <tr className="small">
                                        <td colSpan={8} className="text-start py-2">
                                            <div className="d-flex gap-3 align-items-start flex-wrap">
                                                {/* LEFT: screenshot */}
                                                <div className="border rounded-2 p-1 bg-light" style={{ maxWidth: 360 }}>
                                                    {r.screenshot_path ? (
                                                        <img
                                                            src={`${process.env.REACT_APP_API_URL}/promo_bounty/${r.screenshot_path}`}
                                                            alt="submission screenshot"
                                                            className="img-fluid rounded-2"
                                                        />
                                                    ) : (
                                                        <div className="p-3 small text-muted">No screenshot</div>
                                                    )}
                                                </div>

                                                {/* RIGHT: note + status + payout */}
                                                <div className="flex-grow-1">
                                                    <div className="p-2 rounded-2 border border-1 border-secondary-subtle bg-light-subtle mb-2">
                                                        <div className="text-muted fw-semibold mb-1">Moderator note:</div>
                                                        <div className="text-muted">{r.moderator_note || "—"}</div>
                                                    </div>

                                                    <div className="d-flex flex-wrap align-items-center gap-3 small">
                                                        <div>
                                                            <span className="text-muted me-1">Status:</span>
                                                            <span className={`text-uppercase fw-bold ${
                                                                r.status === "approved" ? "text-success" :
                                                                    r.status === "pending"  ? "text-warning" :
                                                                        r.status === "rejected" ? "text-danger"  : "text-secondary"
                                                            }`}>
                {String(r.status)}
              </span>
                                                        </div>

                                                        <div>
              <span className="text-muted me-1">
                {typeof r.final_payout === "number" ? "Final payout:" : "Estimated payout:"}
              </span>
                                                            <span className="fw-bold text-success">
                {typeof r.final_payout === "number"
                    ? r.final_payout
                    : (r.est_payout ?? (r.is_active_link ? BOUNTY.base + BOUNTY.activeLinkBonus : BOUNTY.base))
                } Virt
              </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}

                            </React.Fragment>
                        ))}
                        </tbody>
                    </Table>
                )}
            </div>

            <div className="small text-muted mt-2">
                <strong>Rules recap:</strong> each unique post is paid once; reposting the same text/link to the same place doesn’t count. Partner links are allowed. Removal of the post may void the payout. The team may adjust amounts for exceptional quality or clearly promotional spam.
            </div>
        </Card>
    );
};

export default VirtalibryPromoBounty;
